import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Flex } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod/dist/zod';

import Button from '../../../../../../components/_core/Button';
import * as FormComponents from '../../../../../../components/_core/form';
import IconButton from '../../../../../../components/_core/IconButton';
import SunshineCard from '../../../../../../components/_core/SunshineCard';
import SquircleIcon from '../../../../../../components/_core/SunshineSquircleIcon';
import Text from '../../../../../../components/_core/Text';
import Form from '../../../../../../components/Form';
import useInvitationActions from '../../hooks/useInvitationActions/useInvitationActions';
import { Beneficiary } from '../../types';
import ResendBeneficiaryInvitationButton from '../ResendBeneficiaryInvitationButton';
import locales from './locales';
import beneficiaryCardFormSchema from './schema';

interface BeneficiaryCardProps {
  beneficiary: Beneficiary;
  currentBeneficiariesEmails: Set<string>;
}

const BeneficiaryCard = ({
  beneficiary,
  currentBeneficiariesEmails,
}: BeneficiaryCardProps) => {
  const isMissingEmail =
    beneficiary.invitation && !beneficiary.invitation.inviteeEmail;

  const canResendInvitation =
    !isMissingEmail && Boolean(beneficiary.invitation?.canResendInvite);

  const [isEditingEmail, setIsEditingEmail] = useState(false);

  const { loading, resend, updateEmail } = useInvitationActions({
    beneficiary,
  });

  const canEdit = beneficiary.onboardingStatus === 'NOT_STARTED';
  const hasError = beneficiary.onboardingStatus === 'INVALID';

  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm({
    defaultValues: { email: beneficiary.invitation?.inviteeEmail ?? '' },
    mode: 'onChange',
    resolver: zodResolver(
      beneficiaryCardFormSchema({
        canResendInvite: beneficiary.invitation?.canResendInvite,
        currentBeneficiariesEmails,
        initialEmailValue: beneficiary.invitation?.inviteeEmail ?? '',
      }),
    ),
  });

  const submitHandler = handleSubmit(async (values) => {
    await updateEmail(values.email);
    setIsEditingEmail(false);
  });

  const renderCardDescription = () => {
    if (isEditingEmail) {
      return null;
    }

    if (isMissingEmail) {
      return (
        <SunshineCard.Text color="orange.800">
          {locales.missingEmail}
        </SunshineCard.Text>
      );
    }

    return (
      <SunshineCard.Text color="grey.600">
        {beneficiary.invitation?.inviteeEmail}
      </SunshineCard.Text>
    );
  };

  return (
    <SunshineCard>
      {hasError || (isMissingEmail && !isEditingEmail) ? (
        <SunshineCard.Slot name="aside">
          <SquircleIcon
            colorScheme="orange"
            iconColor="orange.800"
            margin="auto"
            name="warning"
          />
        </SunshineCard.Slot>
      ) : null}

      <SunshineCard.Content>
        <Flex justifyContent="space-between" width="100%">
          <Box flex={1}>
            <SunshineCard.Text
              fontWeight="weight-500"
              size="md"
              variant="primary"
            >
              {`${beneficiary.firstName} ${beneficiary.lastName}`}
            </SunshineCard.Text>
            {renderCardDescription()}
          </Box>

          {canEdit && !isEditingEmail ? (
            <IconButton
              aria-label={locales.editEmail}
              icon="edit"
              iconSize="icon-16"
              onClick={() => setIsEditingEmail(true)}
              size="sm"
              variant="secondary"
            />
          ) : null}
        </Flex>

        {isEditingEmail ? (
          <Box marginTop="space-10" width="100%">
            <Form onValidSubmit={submitHandler}>
              <FormComponents.Field
                error={errors.email?.message}
                label={locales.inputEmailLabel}
              >
                <FormComponents.Input
                  disabled={loading}
                  type="email"
                  {...register('email')}
                />
              </FormComponents.Field>
              <Box
                display="flex"
                gap="space-8"
                justifyContent="flex-end"
                marginTop="space-16"
                width="100%"
              >
                <Button
                  isDisabled={loading}
                  onClick={() => {
                    reset();
                    setIsEditingEmail(false);
                  }}
                  variant="secondary"
                >
                  {locales.cancel}
                </Button>
                <Button isLoading={loading} type="submit" variant="primary">
                  {isMissingEmail
                    ? locales.sendInvitation
                    : locales.resendInvitation}
                </Button>
              </Box>
            </Form>
          </Box>
        ) : null}
        {!isMissingEmail && canEdit && !isEditingEmail ? (
          <>
            <Box marginY="space-8">
              <ResendBeneficiaryInvitationButton
                canResend={canResendInvitation}
                isSending={loading}
                onClick={resend}
              />
            </Box>

            {!canResendInvitation ? (
              <Text size="sm">{locales.resendInvitationInfo}</Text>
            ) : null}
          </>
        ) : null}
      </SunshineCard.Content>
    </SunshineCard>
  );
};

export default BeneficiaryCard;
